import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Tag, message, Spin, Typography, Space } from "antd";
import { ExportOutlined, BookOutlined, CheckCircleOutlined } from "@ant-design/icons";
import SafeHTMLContent from "../../components/SafeHTMLContent";

import dayjs from "dayjs";
import { getPost, exportPost } from "../../api/mockData";

const { Text } = Typography;

export default function PostDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);

  // 加载文章和书签
  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      try {
        const postData = await getPost(id);
        setPost(postData);
        
        // 加载书签
        const savedBookmarks = JSON.parse(localStorage.getItem("postBookmarks") || "{}");
        if (savedBookmarks[id]) {
          setIsBookmarked(true);
          
          // 自动滚动到书签位置
          setTimeout(() => {
            window.scrollTo({
              top: savedBookmarks[id].position,
              behavior: "smooth"
            });
          }, 100);
        }
      } catch (error) {
        console.error('Error loading post:', error);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  // 处理导出文章
  const handleExport = async () => {
    if (!post) return;
    
    setExporting(true);
    try {
      const result = await exportPost(post.id);
      if (result.success) {
        message.success('文章导出成功！');
      } else {
        message.error('导出失败：' + result.message);
      }
    } catch (error) {
      console.error('Export error:', error);
      message.error('导出过程中发生错误');
    } finally {
      setExporting(false);
    }
  };

  // 处理书签
  const handleBookmark = () => {
    const scrollTop = window.scrollY;
    const savedBookmarks = JSON.parse(localStorage.getItem("postBookmarks") || "{}");
    
    if (isBookmarked) {
      // 移除书签
      delete savedBookmarks[id];
      setIsBookmarked(false);
      message.success("书签已移除");
    } else {
      // 添加书签
      savedBookmarks[id] = {
        position: scrollTop,
        title: post.title,
        timestamp: new Date().toISOString()
      };
      setIsBookmarked(true);
      message.success("书签已保存");
    }
    
    localStorage.setItem("postBookmarks", JSON.stringify(savedBookmarks));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="card-glow" style={{ padding: 20 }}>
        <div className="page-title">文章不存在</div>
        <Button onClick={() => nav("/")}>返回首页</Button>
      </div>
    );
  }

  return (
    <div className="card-glow post-detail-container" style={{ padding: 20, width: "100%" }}>
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: 'wrap', gap: 16 }}>
          <div className="post-detail-title" style={{ fontSize: 32, fontWeight: 750, flex: 1, minWidth: 200, color: "var(--theme-text) !important" }}>
            {post.title}
          </div>
          <Space>
            <Button 
              icon={isBookmarked ? <CheckCircleOutlined /> : <BookOutlined />} 
              onClick={handleBookmark}
              type={isBookmarked ? "primary" : "default"}
              style={{ alignSelf: 'flex-start' }}
            >
              {isBookmarked ? "已书签" : "书签"}
            </Button>
            <Button 
              icon={<ExportOutlined />} 
              onClick={handleExport}
              loading={exporting}
              type="primary"
              style={{ alignSelf: 'flex-start' }}
            >
              导出文章
            </Button>
          </Space>
        </div>

        <Space size={8} wrap>
          {(post.tags || []).map((t) => (
            <Tag key={t} className="post-detail-tag">{t}</Tag>
          ))}
          <Text className="muted">发布于 {dayjs(post.createdAt).format("YYYY-MM-DD HH:mm")}</Text>
        </Space>

        {post.summary && (
          <Card 
            size="small"
            className="post-detail-summary-card"
            style={{ 
              backgroundColor: "var(--theme-card-bg) !important", 
              borderLeft: "4px solid var(--theme-primary) !important"
            }}
          >
            <Text strong className="post-detail-summary-label" style={{ color: "var(--theme-text) !important" }}>摘要：</Text>
            <Text className="post-detail-summary-content" style={{ marginLeft: 8, color: "var(--theme-text) !important" }}>
              {post.summary}
            </Text>
          </Card>
        )}

        {/* 使用 SafeHTMLContent 组件渲染富文本内容 */}
        <Card
          className="post-detail-content-card"
          style={{
            marginBottom: 24,
            background: "var(--theme-card-bg)",
            border: "1px solid var(--theme-card-border)"
          }}
        >
          <div style={{ padding: 24 }}>
            <SafeHTMLContent 
              html={post.content} 
              className="post-detail-content"
              style={{ 
                fontSize: "16px", 
                lineHeight: 1.7
              }}
            />
          </div>
        </Card>

        <div>
          <Button onClick={() => nav(-1)}>返回</Button>
        </div>
      </Space>
    </div>
  );
}